import { Box, Skeleton, SkeletonGroup } from "@salestrak/ui";
import type { Omittable } from "@salestrak/ui";

/** Body-line counts the entries cycle through, so no two read alike. */
const LINE_COUNTS = [2, 1, 3, 1] as const;

export type ActivityListSkeletonProps = Omittable<{
  /** How many feed entries to lay. */
  entries?: number;
  /** Gate the whole feed behind the ~200ms first-paint fade. */
  delayed?: boolean;
}>;

/**
 * The activity feed before its entries arrive: a run of entries in the
 * feed's own grammar — a 20px author glyph, a name-and-time line, then one
 * to three body lines of uneven length. Anonymous by design: an entry may
 * settle as a comment, a stage change or a follow-up, so the bars promise a
 * feed, never a particular kind of entry.
 */
export function ActivityListSkeleton({ entries = 3, delayed = false }: ActivityListSkeletonProps) {
  const body = Array.from({ length: entries }, (_, index) => {
    const lines = LINE_COUNTS[index % LINE_COUNTS.length] ?? 1;
    return (
      <Box key={index} direction="row" gap="sm" align="start">
        <Skeleton circle width={20} />
        <Box direction="column" gap="sm" grow minWidth={0} paddingTop="2xs">
          <Box direction="row" align="center" gap="sm">
            <Skeleton height={12} width={96} />
            <Skeleton height={10} width={48} />
          </Box>
          {Array.from({ length: lines }, (_, line) => (
            <Skeleton height={10} key={line} vary={index + line} width="88%" />
          ))}
        </Box>
      </Box>
    );
  });
  const Wrapper = delayed ? SkeletonGroup : Box;
  return (
    <Wrapper
      className={delayed ? "flex flex-col gap-6" : undefined}
      {...(delayed ? {} : { direction: "column" as const, gap: "lg" as const })}
      data-slot="activity-list-skeleton"
      aria-hidden
    >
      {body}
    </Wrapper>
  );
}
